/**
 * One commercial's sheet: who they are, and the « Gestion commerciale » the customer sheet asks
 * of them — the type of customer they handle, their qualification, the companies they work for.
 *
 * The type and the qualification are picked from the lists an administrator keeps, never typed:
 * the lists exist so « Garage » is spelled once. A value the lists no longer hold is still shown,
 * so opening a sheet never silently clears it.
 *
 * Below the form, the commercial's login and the customers they follow: each acts at once, on
 * its own, without waiting for « Enregistrer ».
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Field } from "@hardauto/core-forms";
import { Button, Checkbox, Dialog, Input, MultiSelect, Select } from "@hardauto/core-ui";

import type { ClientsApi, RepCompany, SalesRep, SalesRepOption } from "../../api";
import { NAMESPACE } from "../../i18n";
import { RepAccessSection } from "./RepAccessSection";
import { RepClientsSection } from "./RepClientsSection";

export interface SalesRepChanges {
  name: string;
  territory: string;
  client_type: string;
  qualification: string;
  company_codes: string[];
  is_active: boolean;
}

export function SalesRepDialog({
  api,
  rep: initial,
  options,
  companies,
  busy,
  mayGiveLogin,
  mayAssign,
  onSave,
  onChanged,
  onClose,
}: {
  api: ClientsApi;
  rep: SalesRep;
  options: readonly SalesRepOption[];
  companies: readonly RepCompany[];
  busy: boolean;
  /** Opening logins and granting profiles: Administration's two rights. */
  mayGiveLogin: boolean;
  mayAssign: boolean;
  onSave: (changes: SalesRepChanges) => void;
  /** The login or the customers changed: the list behind is out of date. */
  onChanged: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const [rep, setRep] = useState(initial);
  const [changes, setChanges] = useState<SalesRepChanges>({
    name: rep.name,
    territory: rep.territory,
    client_type: rep.client_type,
    qualification: rep.qualification,
    company_codes: rep.company_codes,
    is_active: rep.is_active,
  });
  const set = (patch: Partial<SalesRepChanges>) => setChanges((now) => ({ ...now, ...patch }));

  /** The list's values, plus the one this commercial carries if the list no longer has it. */
  const choices = (kind: SalesRepOption["kind"], current: string) => {
    const labels = options.filter((option) => option.kind === kind).map((option) => option.label);
    return current && !labels.includes(current) ? [current, ...labels] : labels;
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("salesReps.edit.title", { name: rep.name })}
      closeLabel={t("salesReps.cancel")}
      footer={
        <>
          <Button onClick={onClose}>{t("salesReps.cancel")}</Button>
          <Button
            variant="primary"
            busy={busy}
            busyLabel={t("salesReps.edit.save")}
            onClick={() => {
              if (changes.name.trim()) onSave(changes);
            }}
          >
            {t("salesReps.edit.save")}
          </Button>
        </>
      }
    >
      <div className="hardauto-form">
        <Field label={t("salesReps.newName")} required>
          {(props) => (
            <Input
              {...props}
              value={changes.name}
              onChange={(event) => set({ name: event.target.value })}
            />
          )}
        </Field>
        <Field label={t("salesReps.newTerritory")}>
          {(props) => (
            <Input
              {...props}
              value={changes.territory}
              onChange={(event) => set({ territory: event.target.value })}
            />
          )}
        </Field>
        <Field label={t("salesReps.column.clientType")}>
          {(props) => (
            <Select
              {...props}
              value={changes.client_type}
              onChange={(event) => set({ client_type: event.target.value })}
            >
              <option value="">{t("salesReps.notSet")}</option>
              {choices("client_type", changes.client_type).map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t("salesReps.column.qualification")}>
          {(props) => (
            <Select
              {...props}
              value={changes.qualification}
              onChange={(event) => set({ qualification: event.target.value })}
            >
              <option value="">{t("salesReps.notSet")}</option>
              {choices("qualification", changes.qualification).map((label) => (
                <option key={label} value={label}>
                  {label}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <MultiSelect
          label={t("salesReps.column.companies")}
          allLabel={t("salesReps.noCompany")}
          options={companies.map((company) => ({
            value: company.code,
            label: company.name || company.code,
          }))}
          selected={changes.company_codes}
          onChange={(next) => set({ company_codes: next })}
          countLabel={(count) => t("salesReps.companiesChosen", { count })}
          clearLabel={t("salesReps.clear")}
          searchLabel={t("salesReps.search")}
        />
        <Checkbox
          label={t("salesReps.state.active")}
          checked={changes.is_active}
          onChange={(event) => set({ is_active: event.target.checked })}
        />
      </div>
      <RepAccessSection
        api={api}
        rep={rep}
        mayGiveLogin={mayGiveLogin}
        onChanged={(updated) => {
          setRep(updated);
          onChanged();
        }}
      />
      <RepClientsSection api={api} rep={rep} mayAssign={mayAssign} onChanged={onChanged} />
    </Dialog>
  );
}
